import type { CanvasItem } from "@bedrock/domain";

export interface CanvasItemSize {
  width: number;
  height: number;
}

export interface CanvasResizeOptions {
  minWidth?: number;
  minHeight?: number;
}

const DEFAULT_CANVAS_ITEM_SIZES = {
  "sticky-note": { width: 270, height: 210 },
  document: { width: 340, height: 450 },
  label: { width: 360, height: 70 },
  image: { width: 380, height: 280 },
  video: { width: 420, height: 260 },
  pdf: { width: 380, height: 490 },
  spreadsheet: { width: 440, height: 300 },
  "web-clip": { width: 360, height: 240 },
  "date-event": { width: 360, height: 230 },
  stack: { width: 290, height: 210 },
  arrow: { width: 320, height: 120 },
  frame: { width: 560, height: 360 }
} satisfies Record<CanvasItem["type"], CanvasItemSize>;

const FRAME_CONTENT_PADDING = 32;

export function defaultCanvasItemSize(type: CanvasItem["type"]): CanvasItemSize {
  return DEFAULT_CANVAS_ITEM_SIZES[type];
}

export function resetCanvasItemSize(item: CanvasItem): CanvasItem {
  return { ...item, ...defaultCanvasItemSize(item.type) };
}

export function fitCanvasFrameToContents(frame: CanvasItem, items: CanvasItem[]): CanvasItem {
  if (frame.props.kind !== "frame") return frame;
  const contents = items.filter((item) => item.parentStackId === frame.id);
  if (!contents.length) return frame;

  const frameRadians = frame.rotation * Math.PI / 180;
  const frameCosine = Math.cos(frameRadians);
  const frameSine = Math.sin(frameRadians);
  const localBounds = contents.reduce((bounds, item) => {
    const itemRadians = item.rotation * Math.PI / 180;
    const itemCosine = Math.cos(itemRadians);
    const itemSine = Math.sin(itemRadians);
    const center = { x: item.x + item.width / 2, y: item.y + item.height / 2 };
    const corners = [
      { x: -item.width / 2, y: -item.height / 2 },
      { x: item.width / 2, y: -item.height / 2 },
      { x: item.width / 2, y: item.height / 2 },
      { x: -item.width / 2, y: item.height / 2 }
    ];

    for (const corner of corners) {
      const world = {
        x: center.x + corner.x * itemCosine - corner.y * itemSine,
        y: center.y + corner.x * itemSine + corner.y * itemCosine
      };
      const local = {
        x: world.x * frameCosine + world.y * frameSine,
        y: -world.x * frameSine + world.y * frameCosine
      };
      bounds.minX = Math.min(bounds.minX, local.x);
      bounds.minY = Math.min(bounds.minY, local.y);
      bounds.maxX = Math.max(bounds.maxX, local.x);
      bounds.maxY = Math.max(bounds.maxY, local.y);
    }
    return bounds;
  }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

  const localCenter = {
    x: (localBounds.minX + localBounds.maxX) / 2,
    y: (localBounds.minY + localBounds.maxY) / 2
  };
  const worldCenter = {
    x: localCenter.x * frameCosine - localCenter.y * frameSine,
    y: localCenter.x * frameSine + localCenter.y * frameCosine
  };
  const width = localBounds.maxX - localBounds.minX + FRAME_CONTENT_PADDING * 2;
  const height = localBounds.maxY - localBounds.minY + FRAME_CONTENT_PADDING * 2;

  return {
    ...frame,
    x: worldCenter.x - width / 2,
    y: worldCenter.y - height / 2,
    width,
    height
  };
}

export function resizeCanvasItemFromScreenDelta(
  item: CanvasItem,
  screenDelta: { x: number; y: number },
  viewportScale: number,
  { minWidth = 140, minHeight = 90 }: CanvasResizeOptions = {}
): CanvasItem {
  const radians = item.rotation * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const worldDelta = { x: screenDelta.x / viewportScale, y: screenDelta.y / viewportScale };
  const localDelta = {
    x: worldDelta.x * cosine + worldDelta.y * sine,
    y: -worldDelta.x * sine + worldDelta.y * cosine
  };
  const width = Math.max(minWidth, item.width + localDelta.x);
  const height = Math.max(minHeight, item.height + localDelta.y);
  const sizeDelta = { x: width - item.width, y: height - item.height };
  const centerShift = {
    x: (sizeDelta.x * cosine - sizeDelta.y * sine) / 2,
    y: (sizeDelta.x * sine + sizeDelta.y * cosine) / 2
  };

  return {
    ...item,
    x: item.x + centerShift.x - sizeDelta.x / 2,
    y: item.y + centerShift.y - sizeDelta.y / 2,
    width,
    height
  };
}
